import { NOTIFICATION_CHANNEL } from "@nlc/shared-types";
import {
  InboxIcon,
  MailIcon,
  MessageSquareIcon,
  PanelTopIcon,
} from "lucide-react";

import { FeaturePageLayout } from "@/features/shared/layout";
import { useUser } from "@/hooks/useUser";
import { useWhitelabel } from "@/hooks/useWhitelabel";

import { useGetNotificationSettings } from "../hooks/useGetNotificationSettings";
import { useGetSuprSendUser } from "../hooks/useGetSuprSendUser";
import { ChannelPreferences } from "./components/ChannelPreferences";
import PauseNotifications from "./components/PauseNotifications";
import ResumeNotifications from "./components/ResumeNotifications";
import { TurnOnNotifications } from "./components/TurnOnNotifications";
import SlackIcon from "./icons/slack";
import WhatsappIcon from "./icons/whatsapp";
import { showSmsChannel } from "./utils/showSmsChannel";

export default function Settings() {
  const { data: notificationSettings, isLoading } =
    useGetNotificationSettings();
  const { data: suprSendUser } = useGetSuprSendUser();

  const { data } = useUser();
  const showSMS = showSmsChannel(
    data?.user.roleId,
    suprSendUser?.properties.$timezone,
  );
  const { isWhitelabel } = useWhitelabel();
  const isTurnOn = suprSendUser?.properties.turnOn === "true";

  const preferences = notificationSettings?.preferences ?? {};

  const channelPreferencesConfig = [
    {
      channel: NOTIFICATION_CHANNEL.INBOX,
      icon: InboxIcon,
      label: "Inbox",
    },
    {
      channel: NOTIFICATION_CHANNEL.EMAIL,
      icon: MailIcon,
      label: "Email",
    },
    {
      channel: NOTIFICATION_CHANNEL.WHATSAPP,
      icon: WhatsappIcon,
      label: "Whatsapp",
    },
    {
      channel: NOTIFICATION_CHANNEL.WEBPUSH,
      icon: PanelTopIcon,
      label: "Browser",
    },
  ];

  if (showSMS) {
    channelPreferencesConfig.push({
      channel: NOTIFICATION_CHANNEL.SMS,
      icon: MessageSquareIcon,
      label: "SMS",
    });
  }

  if (!isWhitelabel) {
    channelPreferencesConfig.push({
      channel: NOTIFICATION_CHANNEL.SLACK,
      icon: SlackIcon,
      label: "Slack",
    });
  }

  return (
    <div className="p-xl">
      <FeaturePageLayout
        headerContent={
          suprSendUser ? (
            <div className="flex flex-row flex-wrap items-center gap-3 ml-auto">
              <TurnOnNotifications user={suprSendUser} />
              <PauseNotifications user={suprSendUser} />
            </div>
          ) : null
        }
        isLoading={isLoading}
        title="Notification Settings"
      >
        <section className="bg-bg-primary p-5">
          <ResumeNotifications user={suprSendUser} />
          <p className="text-text-quaternary font-bold pb-3 text-base">
            Channels
          </p>
          {notificationSettings
            ? channelPreferencesConfig.map((config) => (
                <ChannelPreferences
                  channel={config.channel}
                  enabledCategories={preferences[config.channel] ?? []}
                  Icon={config.icon}
                  isActive={config.channel in preferences}
                  isDisabled={!isTurnOn}
                  key={config.channel}
                  label={config.label}
                />
              ))
            : null}
        </section>
      </FeaturePageLayout>
    </div>
  );
}
